/**
 * Static fixtures fall-back. When the backend is unreachable, the
 * `safeFetch*` helpers return these so the UI can render real shapes.
 *
 * Data is derived from `data/corporate_map_data.json` — minimal subset
 * for the page-render scenarios; full data set is supplied by the live API.
 */

import type { ChangeLog, Country, Entity, RenewalEvent } from "./types";

export const FIXTURE_COUNTRIES: Country[] = [
  { id: "c-fr", name: "France", region: "EMEA", iso2: "FR", lat: 46.2, lng: 2.2, flag: "🇫🇷", _count: { entities: 5 } },
  { id: "c-uk", name: "UK", region: "EMEA", iso2: "GB", lat: 54.0, lng: -2.0, flag: "🇬🇧", _count: { entities: 4 } },
  { id: "c-de", name: "Germany", region: "EMEA", iso2: "DE", lat: 51.1, lng: 10.4, flag: "🇩🇪", _count: { entities: 1 } },
  { id: "c-es", name: "Spain", region: "EMEA", iso2: "ES", lat: 40.4, lng: -3.7, flag: "🇪🇸", _count: { entities: 1 } },
  { id: "c-it", name: "Italy", region: "EMEA", iso2: "IT", lat: 41.9, lng: 12.5, flag: "🇮🇹", _count: { entities: 1 } },
  { id: "c-lu", name: "Luxembourg", region: "EMEA", iso2: "LU", lat: 49.8, lng: 6.1, flag: "🇱🇺", _count: { entities: 2 } },
  { id: "c-nl", name: "Netherlands", region: "EMEA", iso2: "NL", lat: 52.1, lng: 5.3, flag: "🇳🇱", _count: { entities: 1 } },
  { id: "c-pl", name: "Poland", region: "EMEA", iso2: "PL", lat: 52.0, lng: 19.1, flag: "🇵🇱", _count: { entities: 1 } },
  { id: "c-pt", name: "Portugal", region: "EMEA", iso2: "PT", lat: 39.4, lng: -8.2, flag: "🇵🇹", _count: { entities: 1 } },
  { id: "c-ch", name: "Switzerland", region: "EMEA", iso2: "CH", lat: 46.8, lng: 8.2, flag: "🇨🇭", _count: { entities: 1 } },
  { id: "c-be", name: "Belgium", region: "EMEA", iso2: "BE", lat: 50.5, lng: 4.5, flag: "🇧🇪", _count: { entities: 1 } },
  { id: "c-mu", name: "Mauritius", region: "EMEA", iso2: "MU", lat: -20.3, lng: 57.6, flag: "🇲🇺", _count: { entities: 1 } },
  { id: "c-ae", name: "United Arab Emirates", region: "EMEA", iso2: "AE", lat: 24.0, lng: 54.0, flag: "🇦🇪", _count: { entities: 1 } },
  { id: "c-jp", name: "Japan", region: "ASIA", iso2: "JP", lat: 36.2, lng: 138.3, flag: "🇯🇵", _count: { entities: 1 } },
  { id: "c-hk", name: "Hong Kong", region: "ASIA", iso2: "HK", lat: 22.4, lng: 114.1, flag: "🇭🇰", _count: { entities: 1 } },
  { id: "c-sg", name: "Singapore", region: "ASIA", iso2: "SG", lat: 1.3, lng: 103.8, flag: "🇸🇬", _count: { entities: 1 } },
  { id: "c-id", name: "Indonesia", region: "ASIA", iso2: "ID", lat: -0.8, lng: 113.9, flag: "🇮🇩", _count: { entities: 1 } },
  { id: "c-ph", name: "Philippines", region: "ASIA", iso2: "PH", lat: 12.9, lng: 121.8, flag: "🇵🇭", _count: { entities: 1 } },
  { id: "c-th", name: "Thailand", region: "ASIA", iso2: "TH", lat: 15.9, lng: 100.9, flag: "🇹🇭", _count: { entities: 1 } },
  { id: "c-vn", name: "Vietnam", region: "ASIA", iso2: "VN", lat: 14.1, lng: 108.3, flag: "🇻🇳", _count: { entities: 1 } },
  { id: "c-my", name: "Malaysia", region: "ASIA", iso2: "MY", lat: 4.2, lng: 101.9, flag: "🇲🇾", _count: { entities: 1 } },
  { id: "c-do", name: "Dominican Republic", region: "LATAM", iso2: "DO", lat: 18.7, lng: -70.2, flag: "🇩🇴", _count: { entities: 1 } },
  { id: "c-mx", name: "Mexico", region: "LATAM", iso2: "MX", lat: 23.6, lng: -102.5, flag: "🇲🇽", _count: { entities: 1 } },
  { id: "c-br", name: "Brazil", region: "LATAM", iso2: "BR", lat: -14.2, lng: -51.9, flag: "🇧🇷", _count: { entities: 1 } },
  { id: "c-co", name: "Colombia", region: "LATAM", iso2: "CO", lat: 4.6, lng: -74.0, flag: "🇨🇴", _count: { entities: 1 } },
  { id: "c-pe", name: "Peru", region: "LATAM", iso2: "PE", lat: -9.2, lng: -75.0, flag: "🇵🇪", _count: { entities: 1 } },
];

function makeEntity(partial: Partial<Entity> & Pick<Entity, "id" | "name" | "countryId" | "region">): Entity {
  const now = new Date().toISOString();
  const blank: Entity = {
    id: partial.id,
    countryId: partial.countryId,
    region: partial.region,
    name: partial.name,
    companyName: null,
    legalForm: null,
    registrationNo: null,
    vatNo: null,
    city: null,
    businessActivity: null,
    operationalAddress: null,
    registeredOffice: null,
    contact: null,
    ubo: null,
    uboDeclaration: null,
    director: null,
    directorTitle: null,
    signingAuthority: null,
    licence: null,
    issuingAuthority: null,
    licenceNo: null,
    status: "active",
    createdAt: now,
    updatedAt: now,
    country: FIXTURE_COUNTRIES.find((c) => c.id === partial.countryId),
  };
  return { ...blank, ...partial };
}

export const FIXTURE_ENTITIES: Entity[] = [
  makeEntity({ id: "e-gmbh", name: "Hightekers GmbH", countryId: "c-de", region: "EMEA", companyName: "Hightekers GmbH", legalForm: "GmbH", licence: "AÜG", director: "Lars Müller", ubo: "Hightekers SAS" }),
  makeEntity({ id: "e-strid", name: "STRIDEFORTH LTD", countryId: "c-uk", region: "EMEA", companyName: "Strideforth Limited", licence: "Business Registration Certificate", director: "Anne Smith", ubo: "Hightekers Group" }),
  makeEntity({ id: "e-sarl-ch", name: "HIGHTEKERS Sàrl", countryId: "c-ch", region: "EMEA", companyName: "HIGHTEKERS Sàrl", director: "Marc Dupont", ubo: "Hightekers Holding" }),
  makeEntity({ id: "e-sancus", name: "Sancus KK", countryId: "c-jp", region: "ASIA", companyName: "Sancus Kabushiki Kaisha", director: "Yuki Tanaka", ubo: "Sancus Holdings" }),
  makeEntity({ id: "e-serv-do", name: "Serviap Dominicana, S.R.L.", countryId: "c-do", region: "LATAM", legalForm: "S.R.L.", director: "Maria Pérez" }),
  makeEntity({ id: "e-fzllc", name: "Hightekers FZ-LLC", countryId: "c-ae", region: "EMEA", companyName: "Hightekers FZ-LLC", licence: "Commercial License 102772", director: "Omar Al-Hashimi", ubo: "Hightekers SAS" }),
  makeEntity({ id: "e-spain", name: "HIGHTEKERS SPAIN, S.L.", countryId: "c-es", region: "EMEA", companyName: "HIGHTEKERS SPAIN, S.L.", legalForm: "S.L.", director: "Carlos García", ubo: null }),
  makeEntity({ id: "e-srl-it", name: "HIGHTEKERS S.R.L.", countryId: "c-it", region: "EMEA", legalForm: "S.R.L.", director: "Luca Rossi", ubo: "Hightekers Holding" }),
];

export const FIXTURE_RENEWALS: RenewalEvent[] = [
  {
    id: "e-gmbh::License",
    entityId: "e-gmbh",
    entityName: "Hightekers GmbH",
    country: "Germany",
    countryFlag: "🇩🇪",
    region: "EMEA",
    type: "License",
    rawValue: "04/07/2026",
    parsedDate: "2026-07-04T00:00:00.000Z",
    severity: "orange",
    daysUntil: 50,
  },
  {
    id: "e-strid::License",
    entityId: "e-strid",
    entityName: "STRIDEFORTH LTD",
    country: "UK",
    countryFlag: "🇬🇧",
    region: "EMEA",
    type: "License",
    rawValue: "02/06/2026",
    parsedDate: "2026-06-02T00:00:00.000Z",
    severity: "red",
    daysUntil: 18,
  },
  {
    id: "e-sarl-ch::License",
    entityId: "e-sarl-ch",
    entityName: "HIGHTEKERS Sàrl",
    country: "Switzerland",
    countryFlag: "🇨🇭",
    region: "EMEA",
    type: "License",
    rawValue: "08/06/2026",
    parsedDate: "2026-06-08T00:00:00.000Z",
    severity: "red",
    daysUntil: 24,
  },
  {
    id: "e-sancus::License",
    entityId: "e-sancus",
    entityName: "Sancus KK",
    country: "Japan",
    countryFlag: "🇯🇵",
    region: "ASIA",
    type: "License",
    rawValue: "31/12/2024",
    parsedDate: "2024-12-31T00:00:00.000Z",
    severity: "red",
    daysUntil: -500,
  },
  {
    id: "e-serv-do::License",
    entityId: "e-serv-do",
    entityName: "Serviap Dominicana, S.R.L.",
    country: "Dominican Republic",
    countryFlag: "🇩🇴",
    region: "LATAM",
    type: "License",
    rawValue: "03/09/2025",
    parsedDate: "2025-09-03T00:00:00.000Z",
    severity: "red",
    daysUntil: -255,
  },
  {
    id: "e-fzllc::License",
    entityId: "e-fzllc",
    entityName: "Hightekers FZ-LLC",
    country: "United Arab Emirates",
    countryFlag: "🇦🇪",
    region: "EMEA",
    type: "License",
    rawValue: "23/04/2026",
    parsedDate: "2026-04-23T00:00:00.000Z",
    severity: "red",
    daysUntil: -22,
  },
];

export const FIXTURE_CHANGELOG: ChangeLog[] = [
  {
    id: "ch-1",
    entityId: "e-gmbh",
    entity: { name: "Hightekers GmbH" },
    userId: "u-1",
    user: { name: "Muhammed Marvan", email: "muhammed.marvan@hightekers.com" },
    field: "ubo",
    oldValue: "Hightekers Holding",
    newValue: "Hightekers SAS",
    comment: "UBO restructure following May 2026 share transfer.",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
  },
  {
    id: "ch-2",
    entityId: "e-fzllc",
    entity: { name: "Hightekers FZ-LLC" },
    userId: "u-1",
    user: { name: "Muhammed Marvan", email: "muhammed.marvan@hightekers.com" },
    field: "mark_renewed",
    oldValue: "23/04/2025",
    newValue: "23/04/2026",
    comment: "Annual renewal recorded.",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 26).toISOString(),
  },
  {
    id: "ch-3",
    entityId: "e-spain",
    entity: { name: "HIGHTEKERS SPAIN, S.L." },
    userId: "u-1",
    user: { name: "Muhammed Marvan", email: "muhammed.marvan@hightekers.com" },
    field: "director",
    oldValue: "—",
    newValue: "Carlos García",
    comment: "Director appointed at AGM.",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  },
];

import type { Role } from "./permissions";

export interface AdminUserFixture {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  createdAt: string;
  // Soft-delete timestamp — non-null = admin has revoked this user's access.
  // /admin user table marks these rows visually and hides the Revoke button.
  deletedAt: string | null;
}

export const FIXTURE_ADMIN_USERS: AdminUserFixture[] = [
  { id: "u-1", email: "muhammed.marvan@hightekers.com", name: "Muhammed Marvan", role: "legal_advisor", createdAt: "2026-01-10T08:00:00Z", deletedAt: null },
  { id: "u-2", email: "roger.oudiz@hightekers.com",     name: "Roger Oudiz",      role: "legal_advisor", createdAt: "2026-01-12T08:00:00Z", deletedAt: null },
  { id: "u-3", email: "member@hightekers.com",          name: "Team Member",      role: "user",          createdAt: "2026-04-01T08:00:00Z", deletedAt: null },
];
